import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/AISubscription/LoadingSpinner';
import { toast } from 'react-toastify';
import { BASE_URL, API_BASE_URL, institute_id } from '../config/config';
/**
 * AI Study Planner Page
 * Creates personalized study schedules based on courses, deadlines, and learning patterns
 */
const AIStudyPlanner = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { checkFeatureAccess, logFeatureUsage } = useSubscription();
  
  const [goal, setGoal] = useState('');
  const [subjects, setSubjects] = useState('');
  const [deadline, setDeadline] = useState('');
  const [hoursPerDay, setHoursPerDay] = useState(2);
  const [studyPlan, setStudyPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    checkAccess();
  }, []);

  const checkAccess = async () => {
    const access = await checkFeatureAccess('study_planner');
    if (!access.has_access) {
      toast.error(access.message || 'You need to upgrade to use this feature');
      navigate('/subscription/plans');
    }
  };

  const handleGenerate = async () => {
    if (!goal.trim() || !subjects.trim()) {
      toast.error('Please enter your goal and subjects');
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/ai-subscription/features/study-plan/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Institute-ID': user?.institute_id || '',
        },
        body: JSON.stringify({
          goal: goal,
          subjects: subjects,
          deadline: deadline,
          hours_per_day: hoursPerDay,
          user_id: user?.id
        })
      });

      const data = await response.json();

      if (data.success) {
        setStudyPlan(data.data);
        
        await logFeatureUsage('study_planner', {
          goal: goal.substring(0, 50),
          subjects: subjects.substring(0, 50)
        });
        
        toast.success('Study plan generated!');
      } else {
        toast.error(data.message || 'Failed to generate study plan');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to generate study plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setGoal('');
    setSubjects('');
    setDeadline('');
    setHoursPerDay(2);
    setStudyPlan(null);
  };

  const handleDownload = () => {
    if (!studyPlan) return;
    
    const blob = new Blob([studyPlan.plan], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `study-plan-${goal.substring(0, 20)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Study plan downloaded!');
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      <PublicHeader />
      
      <main className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 md:py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/ai/features')}
            className="text-purple-600 hover:text-purple-700 mb-4 flex items-center gap-2"
          >
            ← Back to AI Features
          </button>
          
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
            📅 AI Study Planner
          </h1>
          <p className="text-gray-600">
            Create personalized study schedules optimized for your goals and timeline
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Section */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 sticky top-4">
              <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-4">
                ⚙️ Plan Details
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Goal *
                  </label>
                  <input
                    type="text"
                    value={goal}
                    onChange={(e) => setGoal(e.target.value)}
                    placeholder="e.g., Prepare for Final Exams, Learn Python"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subjects/Topics *
                  </label>
                  <textarea
                    value={subjects}
                    onChange={(e) => setSubjects(e.target.value)}
                    placeholder="e.g., Mathematics, Physics, Chemistry&#10;Or: React, Node.js, MongoDB"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[100px] resize-y"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Target Deadline (Optional)
                  </label>
                  <input
                    type="date"
                    value={deadline}
                    onChange={(e) => setDeadline(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Study Hours Per Day: {hoursPerDay}h
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="8"
                    value={hoursPerDay}
                    onChange={(e) => setHoursPerDay(parseInt(e.target.value))}
                    className="w-full"
                    disabled={loading}
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>1h</span>
                    <span>8h</span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleGenerate}
                    disabled={loading || !goal.trim() || !subjects.trim()}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <LoadingSpinner size="sm" />
                        Generating...
                      </>
                    ) : (
                      <>
                        ✨ Generate Plan
                      </>
                    )}
                  </button>
                  
                  {studyPlan && (
                    <button
                      onClick={handleClear}
                      className="px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition-colors"
                    >
                      New
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Output Section */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-md p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg sm:text-xl font-bold text-gray-800">
                  📋 Your Study Plan
                </h2>
                {studyPlan && (
                  <button
                    onClick={handleDownload}
                    className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                  >
                    📥 Download
                  </button>
                )}
              </div>

              {studyPlan ? (
                <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-4 sm:p-6 border-2 border-purple-200">
                  <div className="prose prose-sm sm:prose max-w-none">
                    <div 
                      className="text-gray-700 whitespace-pre-wrap leading-relaxed"
                      style={{ wordBreak: 'break-word' }}
                    >
                      {studyPlan.plan.split('\n').map((line, idx) => {
                        const formattedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
                        
                        if (line.trim().startsWith('###')) {
                          return (
                            <h3 key={idx} className="font-bold text-xl mt-6 mb-3 text-purple-900">
                              {line.replace(/^###\s*/, '')}
                            </h3>
                          );
                        } else if (line.trim().startsWith('##')) {
                          return (
                            <h2 key={idx} className="font-bold text-2xl mt-8 mb-4 text-purple-800">
                              {line.replace(/^##\s*/, '')}
                            </h2>
                          );
                        } else if (line.trim().startsWith('•') || line.trim().startsWith('-')) {
                          return (
                            <div key={idx} className="ml-4 mb-2" dangerouslySetInnerHTML={{ __html: formattedLine }} />
                          );
                        } else if (line.trim()) {
                          return (
                            <p key={idx} className="mb-3" dangerouslySetInnerHTML={{ __html: formattedLine }} />
                          );
                        }
                        return null;
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center min-h-[500px] text-center p-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <div className="text-6xl mb-4">📅</div>
                  <p className="text-gray-500 text-sm">
                    Your personalized study plan will appear here
                  </p>
                  <p className="text-gray-400 text-xs mt-2">
                    Fill in your details and click "Generate Plan"
                  </p>
                </div>
              )}
            </div>

            {/* Tips Section */}
            {!studyPlan && (
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4 sm:p-6">
                <h3 className="text-lg font-bold text-blue-900 mb-3">
                  💡 Tips for Effective Study Planning
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ul className="space-y-2 text-sm text-blue-800">
                    <li className="flex items-start gap-2">
                      <span>✓</span>
                      <span>Be specific about your learning goals</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span>✓</span>
                      <span>List all topics/subjects you need to cover</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span>✓</span>
                      <span>Set realistic daily study hours</span>
                    </li>
                  </ul>
                  <ul className="space-y-2 text-sm text-blue-800">
                    <li className="flex items-start gap-2">
                      <span>✓</span>
                      <span>Include buffer time for revision</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span>✓</span>
                      <span>Follow the plan consistently</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span>✓</span>
                      <span>Adjust as needed based on progress</span>
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AIStudyPlanner;
